import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Description as DocIcon,
  UploadFile as UploadFileIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { editableSetupCategories } from '../../utils/setupPermissions';
import Footer from '../../components/Footer/Footer';

const CATEGORIES = [
  { key: 'roles',         label: 'Roles',                    nameLabel: 'Role Name',          descLabel: 'Role Description' },
  { key: 'entity_types',  label: 'Entity Types',             nameLabel: 'Entity Type Name',   descLabel: 'Description' },
  { key: 'currencies',    label: 'Currencies',               nameLabel: 'Currency Code',      descLabel: 'Currency Name' },
  { key: 'wealth_sources',label: 'Sources of wealth/funds',  nameLabel: 'Source Name',        descLabel: 'Description' },
  { key: 'service_types', label: 'Supplier Service Types',   nameLabel: 'Service Type Name',  descLabel: 'Description' },
  { key: 'bank_names',  label: 'Bank Names',  nameLabel: 'Bank Name',       descLabel: 'Description' },
  { key: 'departments', label: 'Departments', nameLabel: 'Department Name', descLabel: 'Description' },
];

// All document fields available for entity type configuration
const AVAILABLE_DOCS = [
  { field: 'businessPermit',             label: 'Business Permit / Trading Licence',              defaultType: 'single' },
  { field: 'certificateOfIncorporation', label: 'Certificate of Incorporation or Registration',   defaultType: 'single' },
  { field: 'kraPinCertificate',          label: 'PIN Certificate',                                defaultType: 'single' },
  { field: 'etimsProof',                 label: 'Proof of Registration on e-TIMS',                defaultType: 'single' },
  { field: 'financialStatements',        label: 'Annual Audited Financial Statements',            defaultType: 'single' },
  { field: 'cr12',                       label: 'CR12 (not more than 30 days old)',               defaultType: 'single' },
  { field: 'companyProfile',             label: 'Company Profile',                                defaultType: 'single' },
  { field: 'bankReferenceLetter',        label: 'Bank Reference Letter',                          defaultType: 'single' },
  { field: 'directorsIds',               label: "Directors' IDs / Copies of Passports",           defaultType: 'multiple' },
  { field: 'partnershipDeed',            label: 'Partnership Deed',                               defaultType: 'single' },
  { field: 'partnersPinCertificate',     label: "Partners' PIN Certificate",                      defaultType: 'single' },
  { field: 'partnersTaxCompliance',      label: "Partners' Tax Compliance Certificate",           defaultType: 'single' },
  { field: 'partnerIds',                 label: "Partners' IDs / Copies of Passports",            defaultType: 'multiple' },
  { field: 'shareCertificate',           label: 'Valid Share Certificate',                        defaultType: 'single' },
  { field: 'registryExtract',            label: 'Valid Registry Extract',                         defaultType: 'single' },
  { field: 'taxComplianceCertificate',   label: 'Tax Compliance Certificate',                     defaultType: 'single' },
  { field: 'directorsNationalIds',       label: "Directors' National Identification Documents",   defaultType: 'multiple' },
  { field: 'directorsPassports',         label: "Directors' Passports",                           defaultType: 'multiple' },
  { field: 'nationalId',                 label: 'National Identification Card',                   defaultType: 'single' },
  { field: 'passportDocument',           label: 'Passport',                                       defaultType: 'single' },
  { field: 'workPermit',                 label: 'Work Permit (for foreigners)',                   defaultType: 'single' },
  { field: 'policeClearance',            label: 'Police Clearance Certificate',                   defaultType: 'single' },
  { field: 'resume',                     label: 'Resume (Curriculum Vitae)',                      defaultType: 'single' },
  { field: 'trustDeed',                  label: 'Trust Deed',                                     defaultType: 'single' },
  { field: 'founderPin',                 label: "Founders' PIN Certificate",                      defaultType: 'single' },
  { field: 'foundersIds',                label: "Founders' IDs / Copies of Passports",            defaultType: 'multiple' },
  { field: 'beneficiariesIds',           label: "Beneficiaries' IDs / Copies of Passports",      defaultType: 'multiple' },
];

const EMPTY_FORM = { name: '', description: '' };
const EMPTY_DOC  = { field: '', label: '', uploadType: 'single', required: true };

// A document that came from the KYC policy table carries a documentType, which
// only the seeder and the reconcile script write. Admin-added documents have
// none. That is the whole of the difference between a row that may be edited
// here and one that may not.
//
// This replaces a hard-coded "predefined" pair (business permit, bank reference
// letter) that was prepended to EVERY entity type and forced required —
// including foreign companies, sole proprietors and trusts, which policy does
// not ask for a business licence at all.
const isPolicyDoc = (doc) => Boolean(doc?.documentType);

// The same three answers the Information table gives for a field, worded for a
// document: "Not asked for" is what an admin reaches for when an entity type
// simply does not have the thing, and optional could not say that.
const DocStateSelect = ({ value, onChange }) => (
  <Select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    size="small"
    sx={{ fontSize: '13px', minWidth: 140 }}
  >
    <MenuItem value="required" sx={{ fontSize: '13px' }}>Required</MenuItem>
    <MenuItem value="optional" sx={{ fontSize: '13px' }}>Optional</MenuItem>
    <MenuItem value="hidden" sx={{ fontSize: '13px' }}>Not asked for</MenuItem>
  </Select>
);

// Convert a human label into a camelCase field key
const toLabelKey = (label) =>
  label
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('');

// Wizard step names, so an admin sees where a field appears rather than an index.
const STEP_NAMES = {
  0: 'Basic Information',
  1: 'Payment Details',
  2: 'Entity Details',
  3: 'Declarations',
};

// ─── Document management dialog ───────────────────────────────────────────────
const EntityTypeDocsDialog = ({ item, onClose, onSaved }) => {
  const [policyDocs, setPolicyDocs] = useState(
    (item?.documents || []).filter(isPolicyDoc)
  );
  const [docs, setDocs]         = useState(
    (item?.documents || []).filter((d) => !isPolicyDoc(d))
  );
  const [fields, setFields] = useState(item?.fields || []);
  const [newDoc, setNewDoc]     = useState(EMPTY_DOC);
  const [docInputValue, setDocInputValue] = useState('');
  const [saving, setSaving]     = useState(false);

  // What an admin is about to relax, held until they confirm. Relaxing a KYC
  // requirement is a compliance decision, so it is deliberately two actions.
  const [confirming, setConfirming] = useState(null);

  // One control, three answers, the same three the Information table below
  // offers. Optional and "not asked for" are different decisions: an optional
  // document still sits on the supplier's checklist waiting to be filled.
  const docState = (doc) => {
    if (doc.enabled === false) return 'hidden';
    return doc.required !== false ? 'required' : 'optional';
  };

  const withDocState = (doc, state) => ({
    ...doc,
    enabled: state !== 'hidden',
    required: state === 'required',
  });

  // How much is being asked for, so a change can be told to be a relaxation
  // without enumerating the pairs.
  const DEMAND = { required: 2, optional: 1, hidden: 0 };

  const setDocState = (field, state) => {
    const doc = [...policyDocs, ...docs].find((d) => d.field === field);
    // Every step down stays two deliberate actions, including optional ->
    // not asked for: checking only against `required` would have let an admin
    // relax a document once and then switch it off with no second thought.
    // Tightening one back up needs no ceremony.
    if (doc.policyRequired && DEMAND[state] < DEMAND[docState(doc)]) {
      setConfirming({ kind: 'document', field, label: doc.label, state });
      return;
    }
    const apply = (prev) => prev.map((d) => (d.field === field ? withDocState(d, state) : d));
    if (isPolicyDoc(doc)) setPolicyDocs(apply); else setDocs(apply);
  };

  const setFieldState = (field, state) => {
    const f = fields.find((x) => x.field === field);
    if (state !== 'required' && f.policyRequired) {
      setConfirming({ kind: 'field', field, label: f.label, state });
      return;
    }
    setFields((prev) => prev.map((x) => (x.field === field ? { ...x, state } : x)));
  };

  const applyConfirmed = () => {
    if (confirming.kind === 'document') {
      const apply = (prev) => prev.map((d) =>
        (d.field === confirming.field ? withDocState(d, confirming.state) : d));
      if (policyDocs.some((d) => d.field === confirming.field)) setPolicyDocs(apply);
      else setDocs(apply);
    } else {
      setFields((prev) => prev.map((x) =>
        x.field === confirming.field ? { ...x, state: confirming.state } : x));
    }
    setConfirming(null);
  };

  const handleFieldSelect = (selectedOption) => {
    if (!selectedOption) {
      setNewDoc(EMPTY_DOC);
      return;
    }
    if (selectedOption.isNew) {
      const label = selectedOption.label;
      const field = toLabelKey(label) || `custom_${Date.now()}`;
      setNewDoc((p) => ({ ...p, field, label, uploadType: 'single' }));
    } else {
      setNewDoc((p) => ({
        ...p,
        field:      selectedOption.field,
        label:      selectedOption.label,
        uploadType: selectedOption.defaultType || 'single',
      }));
    }
  };

  const handleAddDoc = () => {
    if (!newDoc.field) { toast.error('Select or type a document type'); return; }
    if (docs.some((d) => d.field === newDoc.field)) {
      toast.error('This document is already in the list'); return;
    }
    setDocs((prev) => [...prev, { ...newDoc }]);
    setNewDoc(EMPTY_DOC);
    setDocInputValue('');
  };

  const handleRemoveDoc = (field) =>
    setDocs((prev) => prev.filter((d) => d.field !== field));


  const handleToggleUploadType = (field) =>
    setDocs((prev) =>
      prev.map((d) =>
        d.field === field
          ? { ...d, uploadType: d.uploadType === 'single' ? 'multiple' : 'single' }
          : d
      )
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/setup-config/${item._id}`, {
        name: item.name,
        description: item.description || '',
        documents: [...policyDocs, ...docs],
        fields,
      });
      toast.success('Documents updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Fields not already on this entity type, in either list.
  const availableToAdd = AVAILABLE_DOCS.filter(
    (d) => !policyDocs.some((p) => p.field === d.field)
      && !docs.some((existing) => existing.field === d.field)
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
            Documents — {item.name}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '13px' }}>
            Define which documents suppliers must upload for this entity type
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Policy documents — set by the KYC checklist, not editable here */}
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Document</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Upload</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Asked for</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {policyDocs.map((doc) => (
              <TableRow key={doc.field} sx={{ bgcolor: '#f9fafb', '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#374151' }}>{doc.label}</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>{doc.field}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.uploadType === 'multiple' ? 'Multiple' : 'Single'}
                    size="small"
                    sx={{ fontSize: '11px', bgcolor: '#e0f2fe', color: '#0369a1' }}
                  />
                </TableCell>
                <TableCell>
                  <DocStateSelect
                    value={docState(doc)}
                    onChange={(state) => setDocState(doc.field, state)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={doc.policyRequired ? 'Required by KYC policy' : 'Policy'}
                    size="small"
                    sx={{
                      fontSize: '11px',
                      bgcolor: doc.policyRequired ? '#fef3c7' : '#f3f4f6',
                      color: doc.policyRequired ? '#92400e' : '#6b7280',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ mb: 2 }} />

        {/* Configurable documents */}
        {docs.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
            No additional documents configured yet.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Upload</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Asked for</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((doc) => (
                <TableRow key={doc.field} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#111827' }}>{doc.label}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>{doc.field}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.uploadType === 'single' ? 'Single' : 'Multiple'}
                      size="small"
                      onClick={() => handleToggleUploadType(doc.field)}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '11px',
                        bgcolor: doc.uploadType === 'single' ? '#e0f2fe' : '#fef3c7',
                        color:   doc.uploadType === 'single' ? '#0369a1' : '#92400e',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <DocStateSelect
                      value={docState(doc)}
                      onChange={(state) => setDocState(doc.field, state)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleRemoveDoc(doc.field)} sx={{ color: '#c62828' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Add new document row */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1.5, fontSize: '13px' }}>
          Add Document
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 300 }}
            options={availableToAdd}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
            inputValue={docInputValue}
            onInputChange={(_, val) => setDocInputValue(val)}
            onChange={(_, val) => handleFieldSelect(val)}
            filterOptions={(options, { inputValue }) => {
              const filtered = options.filter((o) =>
                o.label.toLowerCase().includes(inputValue.toLowerCase())
              );
              if (inputValue.trim() && !options.some((o) => o.label.toLowerCase() === inputValue.trim().toLowerCase())) {
                filtered.push({ field: '__new__', label: inputValue.trim(), isNew: true });
              }
              return filtered;
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.isNew ? '__new__' : option.field}>
                {option.isNew ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#578A18' }}>
                    <AddIcon fontSize="small" />
                    <Typography sx={{ fontSize: '13px' }}>Add "{option.label}"</Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '13px' }}>{option.label}</Typography>
                )}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search or add document type..." size="small" />
            )}
            isOptionEqualToValue={(opt, val) => opt.field === val?.field}
            noOptionsText={
              docInputValue.trim()
                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#578A18', cursor: 'pointer', py: 0.5 }}
                    onClick={() => handleFieldSelect({ field: '__new__', label: docInputValue.trim(), isNew: true })}>
                    <AddIcon fontSize="small" />
                    <Typography sx={{ fontSize: '13px' }}>Add "{docInputValue.trim()}"</Typography>
                  </Box>
                : 'No options'
            }
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={newDoc.uploadType}
              onChange={(e) => setNewDoc((p) => ({ ...p, uploadType: e.target.value }))}
            >
              <MenuItem value="single">Single file</MenuItem>
              <MenuItem value="multiple">Multiple files</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={newDoc.required}
                onChange={(e) => setNewDoc((p) => ({ ...p, required: e.target.checked }))}
                size="small"
                sx={{ color: '#578A18', '&.Mui-checked': { color: '#578A18' } }}
              />
            }
            label={<Typography sx={{ fontSize: '13px', color: '#374151' }}>Required</Typography>}
            sx={{ m: 0 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddDoc}
            disabled={!newDoc.field}
            sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151', height: 40 }}
          >
            Add
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827', mb: 1 }}>
          Information
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#6b7280', mb: 1.5 }}>
          What the application form asks a {item.name} for. Hidden fields do not appear at all.
        </Typography>
        {fields.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
            No information requirements configured. Run the reconcile script to add them.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Step</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '12px' }}>Asked for</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...fields].sort((a, b) => a.step - b.step).map((f) => (
                <TableRow key={f.field}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500, fontSize: '13px', color: '#374151' }}>{f.label}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>{f.field}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>{STEP_NAMES[f.step] || f.step}</Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={f.state || 'required'}
                      onChange={(e) => setFieldState(f.field, e.target.value)}
                      size="small"
                      sx={{ fontSize: '13px', minWidth: 120 }}
                    >
                      <MenuItem value="required" sx={{ fontSize: '13px' }}>Required</MenuItem>
                      <MenuItem value="optional" sx={{ fontSize: '13px' }}>Optional</MenuItem>
                      <MenuItem value="hidden" sx={{ fontSize: '13px' }}>Hidden</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    {f.policyRequired && (
                      <Chip label="Required by KYC policy" size="small"
                        sx={{ fontSize: '11px', bgcolor: '#fef3c7', color: '#92400e' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6b7280' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#578A18', textTransform: 'none', '&:hover': { bgcolor: '#467014' } }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </DialogActions>

      {confirming && (
        <Dialog open onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
            {confirming.state === 'hidden' ? 'Stop asking for this?' : 'Relax a KYC requirement?'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '14px', color: '#374151' }}>
              Betika&apos;s KYC policy requires <strong>{confirming.label}</strong> for{' '}
              <strong>{item.name}</strong>.{' '}
              {confirming.state === 'hidden'
                ? 'It will not appear on the application at all, and nothing already uploaded is removed.'
                : 'Suppliers will no longer have to provide it.'}{' '}
              This change is recorded against your account.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(null)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button onClick={applyConfirmed} variant="contained" color="warning"
              sx={{ textTransform: 'none' }}>
              {confirming.state === 'hidden' ? 'Stop asking' : 'Relax it'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

// ─── Bulk upload preview dialog ───────────────────────────────────────────────
const BulkPreviewDialog = ({ category, preview, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const { toAdd = [], toSkip = [] } = preview;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await api.post('/setup-config/bulk-save', { category, items: toAdd });
      toast.success(`${toAdd.length} item${toAdd.length !== 1 ? 's' : ''} added successfully`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save items');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
            Bulk Upload Preview
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '13px' }}>
            Review what will be added and what will be skipped before confirming
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Items to add */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#578A18', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
              To be added ({toAdd.length})
            </Typography>
          </Box>
          {toAdd.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: '#9ca3af', pl: 3.5 }}>
              No new items to add
            </Typography>
          ) : (
            <Box sx={{ border: '1px solid #d1fae5', borderRadius: 1.5, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#166534' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#166534' }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toAdd.map((item, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{item.name}</TableCell>
                      <TableCell sx={{ fontSize: '13px', color: '#374151' }}>{item.description || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {/* Items to skip */}
        {toSkip.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CancelIcon sx={{ color: '#d97706', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                Skipped — similar item already exists ({toSkip.length})
              </Typography>
            </Box>
            <Box sx={{ border: '1px solid #fde68a', borderRadius: 1.5, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fffbeb' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#92400e' }}>In your file</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#92400e' }}>Matches existing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toSkip.map((item, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '13px', color: '#374151' }}>{item.name}</TableCell>
                      <TableCell sx={{ fontSize: '13px', color: '#374151' }}>
                        <Chip
                          label={item.matchedWith}
                          size="small"
                          sx={{ fontSize: '11px', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6b7280' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || toAdd.length === 0}
          sx={{ bgcolor: '#578A18', textTransform: 'none', '&:hover': { bgcolor: '#467014' } }}
        >
          {saving
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : `Add ${toAdd.length} item${toAdd.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Setups page ─────────────────────────────────────────────────────────
const Setups = () => {
  const { user } = useAuth();
  // Each role sees only the lists it can change; the server enforces the same.
  const categories = CATEGORIES.filter((c) => editableSetupCategories(user?.role).includes(c.key));
  const [activeCategory, setActiveCategory] = useState(categories[0]?.key);
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [editingId, setEditingId]     = useState(null);
  const [docsItem, setDocsItem]       = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const bulkInputRef = useRef(null);

  const activeCategoryMeta = CATEGORIES.find((c) => c.key === activeCategory);
  const isEntityTypes = activeCategory === 'entity_types';

  const fetchItems = useCallback(async () => {
    if (!activeCategory) return;
    setLoading(true);
    try {
      const res = await api.get(`/setup-config/${activeCategory}`);
      setItems(res.data.data || []);
    } catch {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    fetchItems();
  }, [fetchItems]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(`${activeCategoryMeta.nameLabel} is required`);
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/setup-config/${editingId}`, { name: form.name, description: form.description });
        toast.success('Item updated successfully');
      } else {
        await api.post('/setup-config', { category: activeCategory, name: form.name, description: form.description });
        toast.success('Item saved successfully');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setForm({ name: item.name, description: item.description || '' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/setup-config/${id}`);
      toast.success('Item deleted');
      fetchItems();
    } catch {
      toast.error('Failed to delete item');
    }
  };

  const downloadTemplate = () => {
    const meta = CATEGORIES.find(c => c.key === activeCategory);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      [meta.nameLabel, meta.descLabel],
      ['Example Item 1', 'Optional description'],
      ['Example Item 2', ''],
    ]);
    ws['!cols'] = [{ wch: 35 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, `${activeCategory}_bulk_template.xlsx`);
  };

  const handleBulkFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBulkProcessing(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const items = rows
        .slice(1)
        .filter(r => String(r[0] || '').trim())
        .map(r => ({ name: String(r[0]).trim(), description: String(r[1] || '').trim() }));

      if (items.length === 0) {
        toast.error('No valid rows found in the file');
        return;
      }
      const res = await api.post('/setup-config/bulk-preview', { category: activeCategory, items });
      setBulkPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process file');
    } finally {
      setBulkProcessing(false);
    }
  };

  if (!activeCategoryMeta) {
    return (
      <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
            Setups
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Your role has no setup lists to manage.
          </Typography>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
          Setups
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
          View and manage dynamic form items
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Sidebar */}
          <Paper elevation={0} sx={{ width: 220, flexShrink: 0, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
            {categories.map((cat) => (
              <Box
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                sx={{
                  px: 2.5, py: 1.5, cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeCategory === cat.key ? 600 : 400,
                  color:   activeCategory === cat.key ? '#111827' : '#374151',
                  bgcolor: activeCategory === cat.key ? '#f3f4f6' : 'transparent',
                  borderLeft: activeCategory === cat.key ? '3px solid #578A18' : '3px solid transparent',
                  '&:hover': { bgcolor: '#f9fafb' },
                  transition: 'all 0.15s',
                }}
              >
                {cat.label}
              </Box>
            ))}
          </Paper>

          {/* Main content */}
          <Paper elevation={0} sx={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 2, p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 3 }}>
              {activeCategoryMeta.label}
            </Typography>

            {/* Add / Edit form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', mb: 0.75, fontSize: '14px' }}>
                  {activeCategoryMeta.nameLabel}
                </Typography>
                <TextField
                  fullWidth size="small"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', mb: 0.75, fontSize: '14px' }}>
                  {activeCategoryMeta.descLabel}
                </Typography>
                <TextField
                  fullWidth multiline rows={3} size="small"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained" onClick={handleSave} disabled={saving}
                sx={{ bgcolor: '#578A18', textTransform: 'none', fontWeight: 500, '&:hover': { bgcolor: '#467014' } }}
              >
                {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editingId ? 'Update' : 'Save'}
              </Button>
              {editingId && (
                <Button
                  variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit}
                  sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}
                >
                  Cancel
                </Button>
              )}
              {!isEntityTypes && (
                <>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={downloadTemplate}
                    sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151', fontSize: '13px' }}
                  >
                    Download Template
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={bulkProcessing ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    onClick={() => bulkInputRef.current.click()}
                    disabled={bulkProcessing}
                    sx={{ textTransform: 'none', borderColor: '#578A18', color: '#578A18', fontSize: '13px',
                          '&:hover': { bgcolor: '#f0f7e6', borderColor: '#467014' } }}
                  >
                    Bulk Upload
                  </Button>
                  <input
                    ref={bulkInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleBulkFileChange}
                  />
                </>
              )}
            </Box>

            {/* Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: '#578A18' }} />
              </Box>
            ) : items.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#9ca3af', py: 2 }}>No items added yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '13px', borderBottom: '1px solid #e5e7eb' }}>
                      {activeCategoryMeta.nameLabel}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '13px', borderBottom: '1px solid #e5e7eb' }}>
                      {activeCategoryMeta.descLabel}
                    </TableCell>
                    {isEntityTypes && (
                      <TableCell sx={{ fontWeight: 600, color: '#6b7280', fontSize: '13px', borderBottom: '1px solid #e5e7eb' }}>
                        Documents
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#6b7280', fontSize: '13px', borderBottom: '1px solid #e5e7eb' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item._id}
                      sx={{ bgcolor: editingId === item._id ? '#f0f7e6' : 'transparent', '&:last-child td': { border: 0 } }}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{item.name}</TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#374151' }}>{item.description || '—'}</TableCell>
                      {isEntityTypes && (
                        <TableCell>
                          <Chip
                            label={`${(item.documents || []).length} doc${(item.documents || []).length !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ fontSize: '11px', bgcolor: '#f0f7e6', color: '#467014', fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {isEntityTypes && (
                          <IconButton
                            size="small"
                            onClick={() => setDocsItem(item)}
                            title="Manage documents"
                            sx={{ color: '#578A18', mr: 0.5 }}
                          >
                            <DocIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleEdit(item)} sx={{ color: '#6b7280' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(item._id)} sx={{ color: '#c62828' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      </Container>

      {docsItem && (
        <EntityTypeDocsDialog
          item={docsItem}
          onClose={() => setDocsItem(null)}
          onSaved={fetchItems}
        />
      )}

      {bulkPreview && (
        <BulkPreviewDialog
          category={activeCategory}
          preview={bulkPreview}
          onClose={() => setBulkPreview(null)}
          onSaved={fetchItems}
        />
      )}

      <Footer />
    </Box>
  );
};

export default Setups;
